import { useEffect } from "react";
import { RiseIn } from "@/components/site/Reveal";
import { IDS } from "@/lib/testIds";

const SUPPORT_EMAIL = "support@countersign.ca";

export default function Accessibility() {
  useEffect(() => { document.title = "Accessibility — Countersign"; }, []);

  return (
    <main id="main" data-testid={IDS.accessibility.page}>
      {/* ---------- Hero ---------- */}
      <section style={{ padding: "clamp(56px, 7vw, 120px) 0 clamp(40px, 5vw, 80px)", position: "relative", overflow: "hidden" }}>
        <div className="signal-lamp" style={{ opacity: 0.55 }} />
        <div className="container-cs" style={{ position: "relative", maxWidth: 960 }}>
          <RiseIn>
            <div className="chapter-tag" style={{ marginBottom: 22 }}><span className="dot" /> Accessibility</div>
          </RiseIn>
          <RiseIn delay={0.05}>
            <h1
              className="font-display"
              style={{
                fontSize: "clamp(44px, 6.6vw, 92px)",
                margin: 0,
                lineHeight: 1.02,
                letterSpacing: "-0.03em",
                maxWidth: "18ch",
              }}
            >
              Built for people in their <em style={{ color: "var(--accent-ink)" }}>seventies, eighties and nineties.</em>
            </h1>
          </RiseIn>
          <RiseIn delay={0.12}>
            <div className="prose-cs" style={{ marginTop: 28, maxWidth: "60ch" }}>
              <p>Countersign is built for people in their seventies, eighties and nineties, and for the families who worry about them. Most of them are not confident with phones. Some are frightened when they open the app.</p>
              <p>That shaped every screen. This page says what the app supports today, what it does not yet, and why some of the decisions were made the way they were.</p>
            </div>
          </RiseIn>
        </div>
      </section>

      {/* ---------- 01 What Countersign supports today ---------- */}
      <ChapterBlock
        n="01"
        title="What Countersign supports today"
      >
        <SubItem
          title="Larger Text."
          body="Every screen works at the largest accessibility text sizes iOS offers. Content scrolls, the main button stays where it is, and nothing is cut off."
        />
        <SubItem
          title="Sufficient contrast."
          body="Body text is set at a 7:1 contrast ratio against its background, which is higher than the 4.5:1 minimum most apps aim for. Our lightest grey is never used for anything you actually need to read."
        />
        <SubItem
          title="Dark interface."
          body="A full dark colour scheme, not an inverted version of the light one."
        />
        <SubItem
          title="Reduced Motion."
          body="When your device is set to Reduce Motion, Countersign turns off transitions, parallax and slow reveals. Nothing moves that doesn't need to."
          last
        />
      </ChapterBlock>

      {/* ---------- 02 What we are still working on ---------- */}
      <ChapterBlock
        n="02"
        title="What we are still working on"
        intro={
          <p style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "clamp(22px, 2.2vw, 30px)", lineHeight: 1.35, color: "var(--ink-primary)", letterSpacing: "-0.01em", fontVariationSettings: '"opsz" 72, "SOFT" 30, "WONK" 0', margin: 0, maxWidth: "34ch" }}>
            These are not supported yet. We would rather say so than claim them.
          </p>
        }
      >
        <SubItem
          title="VoiceOver."
          body="The labels are written but not yet fully tested. Coming soon."
        />
        <SubItem
          title="Voice Control."
          body="Not yet tested. Coming soon."
        />
        <SubItem
          title="iPad and Apple Watch."
          body="Countersign is an iPhone app today. iPad support is planned, and an Apple Watch version is something we want to build, so you can glance at your wrist while the phone is at your ear."
          last
        />
      </ChapterBlock>

      {/* ---------- 03 Three decisions worth explaining ---------- */}
      <ChapterBlock
        n="03"
        title="Three decisions worth explaining"
      >
        <Decision
          heading="We do not put Face ID in front of your safe word."
          body={
            <>
              <p>Every instinct says to lock a secret behind biometrics. We deliberately did not.</p>
              <p>The moment you need your safe word is the moment someone is on the phone rushing you, pretending to be family, and you are frightened. A Face ID prompt that fails in poor light, or that a shaking hand cannot complete, would defeat the entire point of the app.</p>
              <p>The threat Countersign protects against is a stranger on the phone, not someone holding your device. So the word is one tap away, always.</p>
            </>
          }
        />
        <Decision
          heading="One instruction per screen."
          body={<p>A screen with four sentences on it has three too many. People do not read carefully when they are being pressured, so each screen asks you to do one thing.</p>}
        />
        <Decision
          heading="Contrast higher than the standard."
          body={<p>The usual accessibility target for body text is 4.5:1. We use 7:1. The difference matters when someone is reading a small screen in a dim room, in a hurry, without their glasses.</p>}
          last
        />
      </ChapterBlock>

      {/* ---------- 04 Tell us what is broken ---------- */}
      <section style={{ padding: "clamp(64px, 8vw, 120px) 0 clamp(80px, 10vw, 140px)", background: "var(--surface-sunken)", borderTop: "1px solid var(--separator)" }}>
        <div className="container-cs" style={{ maxWidth: 960 }}>
          <RiseIn>
            <div className="chapter-tag" style={{ marginBottom: 22 }}>
              <span
                className="font-display"
                style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: 22, color: "var(--accent-ink)", fontVariationSettings: '"opsz" 72, "SOFT" 30, "WONK" 0', marginRight: 6 }}
              >
                04
              </span>{" "}
              Tell us what is broken
            </div>
          </RiseIn>
          <RiseIn delay={0.05}>
            <h2
              className="font-display"
              style={{
                fontSize: "clamp(30px, 3.8vw, 52px)",
                margin: 0,
                letterSpacing: "-0.02em",
                lineHeight: 1.08,
                maxWidth: "22ch",
              }}
            >
              If something does not work for you, we want to know.
            </h2>
          </RiseIn>
          <RiseIn delay={0.1}>
            <div className="prose-cs" style={{ marginTop: 24 }}>
              <p>
                If something in Countersign does not work for you, we want to know. Email{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-link" style={{ color: "var(--accent-ink)" }}>
                  {SUPPORT_EMAIL}
                </a>{" "}
                and describe what happened. We read everything.
              </p>
            </div>
          </RiseIn>
        </div>
      </section>
    </main>
  );
}

/* ---------------- shared bits ---------------- */

function ChapterBlock({ n, title, intro, children }) {
  return (
    <section style={{ padding: "clamp(56px, 7vw, 120px) 0", borderTop: "1px solid var(--separator)" }}>
      <div className="container-cs" style={{ maxWidth: 1080 }}>
        <div className="ax-row" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "clamp(28px, 4vw, 72px)", alignItems: "start" }}>
          <div>
            <RiseIn>
              <div
                className="font-display"
                style={{
                  fontFamily: "var(--font-display)",
                  fontStyle: "italic",
                  color: "var(--accent-ink)",
                  fontVariationSettings: '"opsz" 144, "SOFT" 30, "WONK" 0',
                  fontSize: "clamp(48px, 6vw, 88px)",
                  lineHeight: 1,
                  fontWeight: 500,
                }}
              >
                {n}
              </div>
            </RiseIn>
            <RiseIn delay={0.05}>
              <h2 className="font-display" style={{ fontSize: "clamp(30px, 3.8vw, 52px)", margin: "16px 0 0", letterSpacing: "-0.02em", lineHeight: 1.08, maxWidth: "16ch" }}>
                {title}
              </h2>
            </RiseIn>
            {intro ? (
              <RiseIn delay={0.1}>
                <div style={{ marginTop: 24 }}>{intro}</div>
              </RiseIn>
            ) : null}
          </div>
          <div>{children}</div>
        </div>
      </div>
      <style>{`
        @media (min-width: 960px) {
          .ax-row { grid-template-columns: 0.85fr 1.15fr !important; }
        }
      `}</style>
    </section>
  );
}

function SubItem({ title, body, last = false }) {
  return (
    <RiseIn>
      <div
        style={{
          paddingBottom: last ? 0 : "clamp(22px, 2.4vw, 32px)",
          marginBottom: last ? 0 : "clamp(22px, 2.4vw, 32px)",
          borderBottom: last ? "0" : "1px solid var(--separator)",
        }}
      >
        <h3
          className="font-display"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(22px, 2.2vw, 30px)",
            margin: 0,
            letterSpacing: "-0.01em",
            fontWeight: 600,
          }}
        >
          {title}
        </h3>
        <p style={{ marginTop: 10, color: "var(--ink-secondary)", fontSize: 19, lineHeight: 1.65, maxWidth: "56ch" }}>
          {body}
        </p>
      </div>
    </RiseIn>
  );
}

function Decision({ heading, body, last = false }) {
  return (
    <RiseIn>
      <div
        style={{
          paddingBottom: last ? 0 : "clamp(28px, 3vw, 44px)",
          marginBottom: last ? 0 : "clamp(28px, 3vw, 44px)",
          borderBottom: last ? "0" : "1px solid var(--separator)",
        }}
      >
        <h3
          className="font-display"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(24px, 2.6vw, 34px)",
            margin: 0,
            letterSpacing: "-0.01em",
            fontWeight: 600,
            lineHeight: 1.15,
            maxWidth: "26ch",
          }}
        >
          {heading}
        </h3>
        <div className="prose-cs" style={{ marginTop: 16 }}>{body}</div>
      </div>
    </RiseIn>
  );
}
